import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ShoppingCart, Flame, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../hooks/useSettings';

const Header = ({ onMenuClick, transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();
  const { settings } = useSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';
  const showBackButton = !isHome && !location.pathname.includes('/productos') && !location.pathname.includes('/pedidos');

  const bgClass = transparent && !isScrolled
    ? 'bg-transparent'
    : isScrolled || !transparent
    ? 'bg-white/95 backdrop-blur-md shadow-md'
    : 'bg-transparent';

  const textClass = transparent && !isScrolled
    ? 'text-white'
    : 'text-text';

  const businessName = settings?.business_name || 'SammyGas';
  const logo = settings?.logo;

  return (
    <header
      className={`
        fixed top-0 left-0 right-0 z-40
        ${bgClass}
        transition-all duration-300
        pt-safe
      `}
    >
      <div className="px-4 h-16 flex items-center justify-between">
        {/* Left - Menu or Back */}
        <div className="flex items-center">
          {showBackButton ? (
            <button
              onClick={() => window.history.back()}
              className={`
                p-2 -ml-2 rounded-xl
                ${transparent && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-gray-50'}
                transition-colors touch-manipulation active:scale-95
              `}
              aria-label="Volver"
            >
              <ArrowLeft size={24} className={textClass} />
            </button>
          ) : (
            <button
              onClick={onMenuClick}
              className={`
                p-2 -ml-2 rounded-xl
                ${transparent && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-gray-50'}
                transition-colors touch-manipulation active:scale-95
              `}
              aria-label="Abrir menú"
            >
              <Menu size={24} className={textClass} />
            </button>
          )}
        </div>

        {/* Center - Logo */}
        <Link 
          to="/" 
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2"
        >
          {logo ? (
            <img 
              src={logo} 
              alt={businessName}
              className="h-10 w-auto object-contain"
            />
          ) : (
            <>
              <div className={`
                w-10 h-10 rounded-xl flex items-center justify-center
                ${transparent && !isScrolled ? 'bg-white/20' : 'bg-primary'}
                transition-colors
              `}>
                <Flame size={24} className="text-white" />
              </div>
              <span className={`
                font-display font-bold text-xl hidden sm:block
                ${textClass}
              `}>
                {businessName}
              </span>
            </>
          )}
        </Link>

        {/* Right - Cart */}
        <Link
          to="/carrito"
          className={`
            relative p-2 -mr-2 rounded-xl
            ${transparent && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-gray-50'}
            transition-colors touch-manipulation active:scale-95
          `}
          aria-label="Carrito"
        >
          <ShoppingCart size={24} className={textClass} />
          {itemCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center animate-scaleIn">
              {itemCount > 9 ? '9+' : itemCount}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
};

export default Header;
